import { Image } from "~/components/reusable/Image"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { api } from "~/utils/api"

import { Alert, AlertTitle } from "../Alert"
import { Button } from "../Button"
import { Card, CardContent, CardHeader, CardTitle } from "../Card"
import { ConfirmationDialog } from "../ConfirmationDialog"
import { Input } from "../Input"
import LoadingOverlay from "../LoadingOverlay"
import { StatusBadge } from "../StatusBadge"
import { Textarea } from "../Textarea"
import { StatusColor } from "~/constants/status"

interface Props {
  organizationId: string
  onOrganizationDeleted: () => void
}

export const OrgMetaDataCard: React.FC<Props> = ({ organizationId, onOrganizationDeleted }) => {
  const utils = api.useUtils()
  const { data: organization, isLoading } = api.auth0.getOrganization.useQuery({
    organizationId,
  })

  const updateOrganization = api.auth0.updateOrganization.useMutation({
    onSuccess: () => {
      toast.success("Organization updated successfully!")
      utils.auth0.getAllOrganizations.invalidate()
      utils.auth0.getOrganization.invalidate({ organizationId })
    },
    onError: () => {
      toast.error("Failed to update organization.")
    },
  })

  const deleteOrganization = api.auth0.deleteOrganization.useMutation({
    onSuccess: () => {
      toast.success("Organization deleted successfully!")
      utils.auth0.getAllOrganizations.invalidate()
      onOrganizationDeleted()
    },
    onError: () => {
      toast.error("Failed to delete organization.")
    },
  })

  const [displayName, setDisplayName] = useState<string>("")
  const [logoUrl, setLogoUrl] = useState<string>("")

  useEffect(() => {
    if (organization) {
      setDisplayName(organization.display_name || "")
      setLogoUrl(organization.branding?.logo_url || "")
    }
  }, [organization])

  const handleSave = () => {
    if (!organization) return

    const updatedData = {
      organizationId: organization.id,
      display_name: displayName,
      branding: {
        logo_url: logoUrl,
      },
    }

    updateOrganization.mutate(updatedData)
  }

  return (
    <Card className="w-full h-fit">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle>Organization Metadata</CardTitle>
        <StatusBadge color={StatusColor.Neutral} text="Auth0" />
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <LoadingOverlay />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex justify-center items-center w-full">
              <div className="shadow-base rounded-full h-[100px] w-[100px]">
                {logoUrl && (
                  <Image
                    className="rounded-full object-scale-down h-full w-full"
                    src={logoUrl}
                    alt="Org image"
                    width={100}
                    height={100}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 pr-1 align-middle">
              <div className="text-sm text-fg-muted flex items-center h-full">Name / ID</div>
              <Input
                value={organization?.name || ""}
                small
                containerClassName="w-full col-span-2"
                disabled
              />

              <div className="text-sm text-fg-muted flex items-center h-full">Display Name</div>
              <Input
                small
                containerClassName="w-full col-span-2"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />

              <div className="text-sm text-fg-muted flex items-center h-full">Logo url</div>
              <Textarea
                containerClassName="w-full col-span-2"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
              />
            </div>

            <Button
              variant="neutral"
              size="sm"
              requires="staff"
              onClick={handleSave}
              className="w-full"
              wrapperClassName="w-full"
            >
              Update
            </Button>

            <div className="flex flex-col gap-2">
              <span className="font-medium">Danger Zone</span>
              <Alert variant="destructive">
                <div className="flex flex-row justify-between items-center">
                  <div className="flex flex-col">
                    <AlertTitle>Delete organization</AlertTitle>
                    <p>
                      The organization will be permanently removed and all members will lose access.
                    </p>
                  </div>
                  <ConfirmationDialog
                    requires="staff"
                    title="Delete Organization"
                    description="Are you sure you want to delete this organization? This action cannot be undone and will remove all members' access."
                    onConfirm={() => {
                      deleteOrganization.mutate({ organizationId, orgId: organization?.name ?? "" })
                    }}
                    trigger={
                      <Button variant="danger" size="sm">
                        Delete Organization
                      </Button>
                    }
                  />
                </div>
              </Alert>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
